/**
 * Single source of truth for each retailer's published price-adjustment
 * claim window (the legally relevant deadline, NOT how long we keep
 * scraping daily). Keep this in sync with `policyDays` on
 * /api/retailers and `retailerLandingData.js`.
 *
 * "Confirmed" means the retailer publishes the window in writing.
 * "Default" means our best estimate from NZ practice when the retailer
 * doesn't publish a number explicitly (the user should ask in store to
 * confirm for their specific item).
 */
export const RETAILER_POLICIES = {
  pb_tech: { name: "PB Tech", policyDays: 30, confidence: "confirmed", source: "PBCare", submitMode: "email", submitTo: "sales@pbtech.co.nz" },
  jb_hifi: { name: "JB Hi-Fi", policyDays: 14, confidence: "default", source: "Most JB Hi-Fi NZ stores honour 14 days on request", submitMode: "form", submitUrl: "https://www.jbhifi.co.nz/pages/help-and-support/contact-us" },
  harvey_norman: { name: "Harvey Norman", policyDays: 7, confidence: "default", source: "Harvey Norman AU published 7-day window; NZ varies by store", submitMode: "email", submitTo: "admin.online@nz.harveynorman.com" },
};

export function policyForRetailer(retailerId) {
  return RETAILER_POLICIES[retailerId] || null;
}

/**
 * Compute the claim-window status for a tracked purchase. Returns a
 * lightweight object the UI can render directly.
 *
 *   { policyDays, claimEndsOn, daysLeftInWindow, windowOpen, source }
 *
 * - `claimEndsOn` is a `Date` (or null if retailer/policy not known).
 * - `daysLeftInWindow` clamps at 0 once the window closes.
 * - `windowOpen` is the simple boolean the UI hangs styling off.
 */
export function claimWindowStatus(retailerId, purchaseDateIso) {
  const policy = policyForRetailer(retailerId);
  if (!policy || !purchaseDateIso) {
    return { policyDays: null, claimEndsOn: null, daysLeftInWindow: null, windowOpen: false, source: null, name: null };
  }
  const purchase = new Date(purchaseDateIso);
  const claimEndsOn = new Date(purchase);
  claimEndsOn.setDate(claimEndsOn.getDate() + policy.policyDays);
  const ms = claimEndsOn - new Date();
  const daysLeftInWindow = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  return {
    policyDays: policy.policyDays,
    claimEndsOn,
    daysLeftInWindow,
    windowOpen: ms > 0,
    source: policy.source,
    name: policy.name,
  };
}
